import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TicketCheck, RefreshCw, Send, Paperclip,
  Download, Clock, Lock, AlertCircle,
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminGetTicket, adminUpdateTicket, adminAddComment } from '../../utils/adminApi';

const STATUS_OPTIONS = [
  { value: 'open',            label: 'Open' },
  { value: 'acknowledged',    label: 'Acknowledged' },
  { value: 'in_progress',     label: 'In Progress' },
  { value: 'waiting_on_user', label: 'Waiting on User' },
  { value: 'resolved',        label: 'Resolved' },
  { value: 'closed',          label: 'Closed' },
  { value: 'wont_fix',        label: "Won't Fix" },
];

const PRIORITY_OPTIONS = [
  { value: 'low',      label: 'Low' },
  { value: 'medium',   label: 'Medium' },
  { value: 'high',     label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const STATUS_COLORS = {
  open:             '#3B82F6',
  acknowledged:     '#8B5CF6',
  in_progress:      '#F59E0B',
  waiting_on_user:  '#EF4444',
  resolved:         '#10B981',
  closed:           '#6B7280',
  wont_fix:         '#DC2626',
};

const PRIORITY_COLORS = {
  low:      'var(--clr-text-muted)',
  medium:   'var(--clr-warning)',
  high:     'var(--clr-error)',
  critical: '#FF0000',
};

const CATEGORIES = {
  wrong_data:  'Wrong / Unexpected Data',
  ui_bug:      'UI / Display Issue',
  performance: 'Slow Performance',
  access:      'Access / Login Issue',
  feature:     'Feature Request',
  other:       'Other',
};

function fmt(iso) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminTicketDetailPage() {
  const { ref }  = useParams();
  const navigate = useNavigate();
  const { adminKey } = useAdminAuth();

  const [ticket,   setTicket]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // Edit state
  const [editStatus,     setEditStatus]     = useState('');
  const [editPriority,   setEditPriority]   = useState('');
  const [editAssigned,   setEditAssigned]   = useState('');
  const [saving,         setSaving]         = useState(false);
  const [saveErr,        setSaveErr]        = useState(null);
  const [saveOk,         setSaveOk]         = useState(false);

  // Reply state
  const [replyBody,     setReplyBody]     = useState('');
  const [isInternal,    setIsInternal]    = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [replyErr,      setReplyErr]      = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetTicket(adminKey, ref);
      setTicket(data);
      setEditStatus(data.status);
      setEditPriority(data.priority);
      setEditAssigned(data.assigned_to || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminKey, ref]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaveErr(null);
    setSaveOk(false);
    try {
      const updated = await adminUpdateTicket(adminKey, ref, {
        status:      editStatus,
        priority:    editPriority,
        assigned_to: editAssigned || null,
      });
      setTicket((t) => ({ ...t, ...updated }));
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2500);
    } catch (e) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSubmitting(true);
    setReplyErr(null);
    try {
      const c = await adminAddComment(adminKey, ref, replyBody.trim(), isInternal);
      setTicket((t) => ({ ...t, comments: [...t.comments, c] }));
      setReplyBody('');
    } catch (e) {
      setReplyErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isDirty = ticket && (
    editStatus   !== ticket.status ||
    editPriority !== ticket.priority ||
    editAssigned !== (ticket.assigned_to || '')
  );

  if (loading) return <div className="admin-page" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--clr-text-muted)' }}>Loading ticket…</div>;
  if (error)   return <div className="admin-page"><div className="admin-error">{error}</div></div>;
  if (!ticket) return null;

  const categoryLabel = CATEGORIES[ticket.category] || ticket.category;

  return (
    <div className="admin-page">
      {/* Back */}
      <button
        className="admin-btn-icon"
        style={{ marginBottom: '1rem', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--clr-text-secondary)' }}
        onClick={() => navigate('/inqlect-admin/support')}
      >
        <ArrowLeft size={15} /> Back to tickets
      </button>

      {/* Header */}
      <div className="atd-card">
        <div className="atd-header-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TicketCheck size={18} style={{ color: 'var(--clr-accent)' }} />
            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: 'var(--clr-accent-light)' }}>
              {ticket.ticket_ref}
            </span>
            <span
              className="admin-badge"
              style={{
                background: `color-mix(in srgb, ${STATUS_COLORS[ticket.status] || '#94A3B8'} 15%, transparent)`,
                color: STATUS_COLORS[ticket.status] || '#94A3B8',
                border: `1px solid color-mix(in srgb, ${STATUS_COLORS[ticket.status] || '#94A3B8'} 35%, transparent)`,
              }}
            >
              {STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label || ticket.status}
            </span>
            <span style={{ color: PRIORITY_COLORS[ticket.priority], fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>
              {ticket.priority}
            </span>
          </div>
          <button className="admin-btn-icon" onClick={load} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>

        <h1 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0.75rem 0 0.5rem', color: 'var(--clr-text-primary)' }}>
          {ticket.title}
        </h1>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <span className="admin-tag">{ticket.tenant_id}</span>
          <span className="admin-tag">{categoryLabel}</span>
          <span style={{ fontSize: 12, color: 'var(--clr-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={11} /> {fmt(ticket.created_at)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--clr-text-muted)' }}>
            by {ticket.user_name || ticket.user_email}
          </span>
        </div>

        <p style={{ fontSize: 13, color: 'var(--clr-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
          {ticket.description}
        </p>
      </div>

      {/* Update panel */}
      <div className="atd-card atd-update-panel">
        <h3 className="atd-section-title">Update Ticket</h3>
        <div className="atd-update-row">
          <label className="atd-label">
            Status
            <select className="admin-input atd-select" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="atd-label">
            Priority
            <select className="admin-input atd-select" value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="atd-label" style={{ flex: 2 }}>
            Assigned To
            <input
              className="admin-input"
              placeholder="email@inqlect.com"
              value={editAssigned}
              onChange={(e) => setEditAssigned(e.target.value)}
            />
          </label>
          <button
            className="admin-btn-primary atd-save-btn"
            onClick={handleSave}
            disabled={saving || !isDirty}
          >
            {saving ? 'Saving…' : saveOk ? 'Saved ✓' : 'Save Changes'}
          </button>
        </div>
        {saveErr && <div className="admin-error" style={{ marginTop: 8 }}>{saveErr}</div>}
      </div>

      {/* Attachments */}
      {ticket.attachments?.length > 0 && (
        <div className="atd-card">
          <h3 className="atd-section-title">
            Attachments
            <span className="atd-count">{ticket.attachments.length}</span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ticket.attachments.map((a) => {
              const attStyle = {
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 12px',
                background: 'var(--clr-bg-elevated)',
                border: '1px solid var(--clr-border-soft)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--clr-accent-light)',
                textDecoration: 'none', fontSize: 13,
              };
              const inner = (
                <>
                  <Paperclip size={13} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.filename}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--clr-text-muted)' }}>{fmtSize(a.file_size)}</span>
                  {a.blob_url && <Download size={12} />}
                </>
              );
              return a.blob_url ? (
                <a key={a.id} href={a.blob_url} target="_blank" rel="noreferrer" style={attStyle}>
                  {inner}
                </a>
              ) : (
                <span key={a.id} style={{ ...attStyle, cursor: 'default', opacity: 0.7 }} title="File stored — download link pending">
                  {inner}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Comment thread */}
      <div className="atd-card">
        <h3 className="atd-section-title">
          Comments
          <span className="atd-count">{ticket.comments?.length || 0}</span>
        </h3>

        {!ticket.comments?.length ? (
          <p style={{ fontSize: 13, color: 'var(--clr-text-muted)', margin: '0 0 1rem' }}>No comments yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: '1rem' }}>
            {ticket.comments.map((c) => (
              <div
                key={c.id}
                className={`atd-comment ${
                  c.is_internal ? 'atd-comment--internal'
                  : c.author_role === 'inqlect' ? 'atd-comment--inqlect'
                  : 'atd-comment--user'
                }`}
              >
                <div className="atd-comment-header">
                  <span className="atd-comment-author">{c.author_name || c.author_email}</span>
                  {c.is_internal && (
                    <span className="atd-tag atd-tag--internal">
                      <Lock size={9} /> Internal
                    </span>
                  )}
                  {!c.is_internal && c.author_role === 'inqlect' && (
                    <span className="atd-tag atd-tag--inqlect">InqLect</span>
                  )}
                  {c.author_role === 'tenant_admin' && (
                    <span className="atd-tag atd-tag--admin">Admin</span>
                  )}
                  <span className="atd-comment-date">{fmt(c.created_at)}</span>
                </div>
                <p className="atd-comment-body">{c.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reply form */}
        <form onSubmit={handleReply} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            className="admin-input admin-textarea"
            rows={3}
            placeholder={isInternal ? 'Internal note (only visible to InqLect team)…' : 'Reply to user…'}
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            maxLength={5000}
          />
          {replyErr && (
            <div className="admin-error" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={13} /> {replyErr}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--clr-text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <Lock size={12} /> Internal note (hidden from tenant)
            </label>
            <button
              type="submit"
              className="admin-btn-primary"
              disabled={submitting || !replyBody.trim()}
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Send size={13} /> {submitting ? 'Sending…' : isInternal ? 'Save Note' : 'Reply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
